import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/auth";
import { useMemo } from "react";
import api from "services/api";
import { ptBR } from '@mui/x-data-grid/locales';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { ITotal } from "./types";
import { Main } from "../CollaboratorsSummary/styled";
import { ErrorOutline } from "@mui/icons-material";

export function CollaboratorsTotal() {
  const auth = useAuth();
  const organizationCnpj = auth.user?.organization?.cnpj;

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["collaboratorsTables"],
    queryFn: () => api.get<{ data: ITotal[] }>(`/v2/cdp/summary-tables/${organizationCnpj}/TOTAL`),
    select: (data) => data.data.data,
    // queryFn: () => Promise.resolve({ data: mockData }),
    // select: (data) => data.data.data as ISummary[],
  });

  const formatDate = (date: any): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("pt-BR");
  };

  const dateValueGetter = (params: any) => {
    if (!params) return null;
    if (params instanceof Date) return params;
    if (typeof params === "string") return new Date(params);
    return null;
  };

  const formatCurrency = (value: number | null): string => {
    if (!value) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatCPF = (cpf: string | null): string => {
    if (!cpf) return "-";
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const columns: GridColDef<ITotal>[] = useMemo(() => [
    {
      field: "ORGANIZATION_ID",
      headerName: "ID Organização",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_ID",
      headerName: "ID Funcionário",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "PERSON_TYPE",
      headerName: "Tipo Pessoa",
      width: 120,
      renderCell: (params) => {
        const type = params.value as string | null;
        if (type === "employee") return "Titular";
        if (type === "dependent") return "Dependente";
        return type || "-";
      },
    },
    {
      field: "DEPENDENT_TYPE_ID",
      headerName: "Tipo Dependente",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "PERSON_TAX_PAYER_ID",
      headerName: "CPF",
      width: 130,
      renderCell: (params) => formatCPF(params.value as string | null),
    },
    {
      field: "PERSON_NAME",
      headerName: "Nome",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "PERSON_BIRTH_DATE",
      headerName: "Data Nascimento",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "PERSON_GENDER_ID",
      headerName: "Gênero",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_PIS",
      headerName: "PIS",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_RACE_ID",
      headerName: "Raça",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_MARITAL_STATUS_ID",
      headerName: "Estado Civil",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_EDUCATION_LEVEL_ID",
      headerName: "Escolaridade",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_SOCIAL_NAME",
      headerName: "Nome Social",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_BIRTH_COUNTRY_ID",
      headerName: "País Nascimento",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_NATIONALITY_COUNTRY_ID",
      headerName: "Nacionalidade",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_BIRTH_STATE_ID",
      headerName: "Estado Nascimento",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_BIRTH_CITY_ID",
      headerName: "Cidade Nascimento",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_MOTHER_NAME",
      headerName: "Nome da Mãe",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_FATHER_NAME",
      headerName: "Nome do Pai",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_ZIP_CODE",
      headerName: "CEP",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_ADDRESS_TYPE",
      headerName: "Tipo Endereço",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_ADDRESS",
      headerName: "Endereço",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_ADDRESS_NUMBER",
      headerName: "Número",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_COMPLEMENT",
      headerName: "Complemento",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_NEIGHBORHOOD",
      headerName: "Bairro",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_STATE_ID",
      headerName: "Estado",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_CITY_ID",
      headerName: "Cidade",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_NATIONAL_IDENTITY_NUMBER",
      headerName: "RG",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_NATIONAL_IDENTITY_EMISSOR",
      headerName: "Órgão Emissor RG",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_NATIONAL_IDENTITY_EXPEDITION_DATE",
      headerName: "Data Expedição RG",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_PHONE_NUMBER",
      headerName: "Telefone",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYEE_EMAIL",
      headerName: "Email",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "DAG_RUN_ID",
      headerName: "ID Execução DAG",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EXECUTION_DATE",
      headerName: "Data Execução",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "EMPLOYEE_TAX_PAYER_ID",
      headerName: "CPF Funcionário",
      width: 150,
      renderCell: (params) => formatCPF(params.value as string | null),
    },
    {
      field: "PERSON_IS_LEGAL_ENTITY",
      headerName: "É Pessoa Jurídica",
      width: 160,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "EMPLOYEE_WEDDING_DATE",
      headerName: "Data Casamento",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "DEPENDENT_IS_STUDENT",
      headerName: "Dependente Estudante",
      width: 170,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    
    // Campos adicionais da tabela employee_labor_contract_datas_raw
    {
      field: "ID",
      headerName: "ID Contrato",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "POSITION",
      headerName: "Posição",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "ROLE",
      headerName: "Papel",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "CBO",
      headerName: "CBO",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "CUMULATIVE_POSITION",
      headerName: "Posição Cumulativa",
      width: 160,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "EMPLOYMENT_AGREEMENT_TYPE_ID",
      headerName: "Tipo Acordo Emprego",
      width: 170,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "IS_EMPLOYEE",
      headerName: "É Funcionário",
      width: 130,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "WORK_WEEK_HOURS",
      headerName: "Horas Semana",
      width: 130,
      type: "number",
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORK_HOURS_TYPE",
      headerName: "Tipo Horário",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORK_PARTIAL_TIME",
      headerName: "Tempo Parcial",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORK_WEEKLY_WEEK_HOURS",
      headerName: "Horas Semanais",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "NIGHT_SHIFT",
      headerName: "Turno Noturno",
      width: 130,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "STATUS",
      headerName: "Status",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "HIRING_DATE",
      headerName: "Data Contratação",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "HIRING_SENT_DATE",
      headerName: "Envio ao eSocial (Admissão)",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "DISMISSAL_DATE",
      headerName: "Data Demissão",
      width: 180,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => {
        const date = params.value;
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "-";
        const isUsed = params.row.EXCLUSION_DATE_TYPE === "DISMISSAL_DATE";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              {date.toLocaleDateString("pt-BR")}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: "primary.main" }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "DISMISSAL_SENT_DATE",
      headerName: "Envio ao eSocial (Desligamento)",
      width: 220,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "END_PRIOR_NOTICE_DATE",
      headerName: "Término Aviso Prévio",
      width: 200,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => {
        const date = params.value;
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        const isUsed = params.row.EXCLUSION_DATE_TYPE === "PRIOR_NOTICE_END_DATE";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              {date.toLocaleDateString("pt-BR")}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "RURAL_LABOUR",
      headerName: "Trabalho Rural",
      width: 140,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "TRANSFERRED_EMPLOYEE",
      headerName: "Funcionário Transferido",
      width: 170,
      type: "boolean",
      renderCell: (params) => {
        const value = params.value as boolean | null;
        return value === null ? "-" : value ? "Sim" : "Não";
      },
    },
    {
      field: "ADMISSION_TYPE",
      headerName: "Tipo Admissão",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EMPLOYMENT_WORK_HOURS",
      headerName: "Horas Trabalho",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "NATURE_OF_ACTIVITY",
      headerName: "Natureza Atividade",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "BASE_MONTH_DATE",
      headerName: "Data Mês Base",
      width: 140,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "LABOR_UNION_FEDERAL_TAX_ID",
      headerName: "CNPJ Sindicato",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "FGTS_OPTION",
      headerName: "Opção FGTS",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "FGTS_OPTION_DATE",
      headerName: "Data Opção FGTS",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "SALARY",
      headerName: "Salário",
      width: 120,
      type: "number",
      // renderCell: (params) => formatCurrency(params.value as number | null),
      renderCell: (params) => "",
    },
    {
      field: "NET_SALARY",
      headerName: "Salário Líquido",
      width: 140,
      type: "number",
      // renderCell: (params) => formatCurrency(params.value as number | null),
      renderCell: (params) => "",
    },
    {
      field: "PAY_PERIOD_ID",
      headerName: "ID Período Pagamento",
      width: 170,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "REGISTRATION_TYPE",
      headerName: "Tipo Registro",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "REGISTRATION_NUMBER",
      headerName: "Número Registro",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "LABOR_REGIME",
      headerName: "Regime Trabalhista",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "LABOR_CATEGORY",
      headerName: "Categoria Trabalhista",
      width: 170,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WELFARE_POLICY_TYPE",
      headerName: "Tipo Política Bem-Estar",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "LABOR_CONTRACT_TYPE",
      headerName: "Tipo Contrato Trabalho",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "LABOR_TERMINATION_DATE",
      headerName: "Data Rescisão Trabalho",
      width: 180,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "CREATED_AT",
      headerName: "Criado Em",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "UPDATED_AT",
      headerName: "Atualizado Em",
      width: 150,
      type: "date",
      valueGetter: dateValueGetter,
      renderCell: (params) => formatDate(params.value),
    },

    // Campos da tabela employee_company_datas_raw
    {
      field: "WORKPLACE_TAX_TYPE",
      headerName: "Tipo Imposto Local",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORPLACE_FEDERAL_TAX_ID",
      headerName: "CNPJ Local Trabalho",
      width: 170,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORKPLACE_NAME",
      headerName: "Nome Local Trabalho",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORKPLACE_FULL_DESCRIPTION",
      headerName: "Descrição Completa Local",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "WORKPLACE_CNAE",
      headerName: "CNAE Local Trabalho",
      width: 160,
      renderCell: (params) => params.value || "-",
    },

    // Campos da tabela payrolls_raw
    {
      field: "YEAR",
      headerName: "Ano",
      width: 80,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "MONTH",
      headerName: "Mês",
      width: 80,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "DEMONSTRATIVE",
      headerName: "Demonstrativo",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "ESTABLISHMENT_CATEGORY",
      headerName: "Categoria Estabelecimento",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "ESTABLISHMENT",
      headerName: "Estabelecimento",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "BATCH",
      headerName: "Lote",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "ENROLLMENT",
      headerName: "Matrícula",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "HARMFUL_AGENTS_EXPOSITION",
      headerName: "Exposição Agentes Nocivos",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "IDENTIFIER_EVENT",
      headerName: "Identificador Evento",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EVENT_RECEIPT",
      headerName: "Recibo Evento",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EVENT_ISSUER",
      headerName: "Emissor Evento",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "EVENT_VERSION",
      headerName: "Versão Evento",
      width: 130,
      renderCell: (params) => params.value || "-",
    },
  ], []);

  const rows: GridRowsProp = useMemo(() => {
    if (!data) return [];
    
    return data.map((item, index) => ({
      id: `${item.PERSON_TAX_PAYER_ID || ''}_${index}`,
      ...item,
    }));
  }, [data]);

  if (!isFetched && isFetching) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={3}
      >
        <CircularProgress size={30} thickness={4} />
      </Box>
    );
  }

  if (!isFetched && isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={3}
        textAlign="center"
      >
        <ErrorOutline sx={{ fontSize: 40, color: 'error.main' }} />
        <Typography variant="h6" color="textPrimary">
          Ops! Algo deu errado
        </Typography>
        <Typography variant="body1" color="textSecondary" maxWidth={400}>
          Não foi possível carregar os dados dos colaboradores no momento. 
          Por favor, tente novamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: 600,
        // backgroundColor: "#FFFFFF",
        // borderRadius: "0.5rem",
        // boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
        // padding: "1rem",
      }}
    >
      <DataGrid
        rows={rows}
        //@ts-ignore
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        // checkboxSelection
        disableRowSelectionOnClick
        localeText={{
          ...ptBR.components.MuiDataGrid.defaultProps.localeText,
          noRowsLabel: 'Nenhum colaborador encontrado',
        }}
        sx={{
          border: 0,
          backgroundColor: "white",
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f5f5f5",
            borderBottom: "2px solid rgba(224, 224, 224, 1)",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            color: "#1E1B39",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(51, 0, 255, 0.04)",
          },
        }}
      />
    </Box>
  );
}
